import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClearHazardDto,
  CreateHazardDto,
  FileHazards,
  HazardMapEntry,
  SafetyBoard,
  SafetyCheckInDto,
  SafetyEventView,
  SafetyStatus,
  SafetyTimerView,
  UpdateHazardDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * Officer safety: the officer's own timer and SOS, the supervisor's board, and
 * hazard flags on a file. Every read and write is gated server-side — the
 * municipality's officer safety switch, `safety:checkin` / `safety:respond`,
 * and the file's own scope for hazards — so these hooks only decide what to
 * show, never what is allowed.
 */
export const safetyKeys = {
  all: ['safety'] as const,
  me: ['safety', 'me'] as const,
  board: (localGovernmentId: string) => ['safety', 'board', localGovernmentId] as const,
  hazards: (fileId: string) => ['safety', 'hazards', fileId] as const,
  hazardMap: ['safety', 'hazard-map'] as const,
  /** Alerts pushed over the socket this session, by id — feeds the shell banner. */
  pushed: ['safety', 'pushed'] as const,
};

export interface Fix {
  lat?: number | null;
  lng?: number | null;
  accuracy?: number | null;
  locatedAt?: string | null;
}

/**
 * One best-effort browser fix, never longer than `timeoutMs`. A desk portal
 * often has no location at all; the server then falls back to the timer's last
 * check-in or live tracking, so a refusal here never blocks anything.
 */
export function currentFix(timeoutMs = 4_000): Promise<Fix> {
  return new Promise((resolve) => {
    const geo = typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
    if (!geo) return resolve({});
    const timer = setTimeout(() => resolve({}), timeoutMs + 500);
    geo.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          locatedAt: new Date(pos.timestamp).toISOString(),
        });
      },
      () => {
        clearTimeout(timer);
        resolve({});
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}

/** A fresh idempotency key for one SOS, reused by every retry of it. */
export function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 from Math.random, for an old browser without randomUUID.
  // cspell:disable-next-line
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function useSafetyStatus(enabled = true) {
  return useQuery({
    queryKey: safetyKeys.me,
    queryFn: async () => (await api.get<SafetyStatus>('/safety/me')).data,
    enabled,
    // The socket invalidates it on every change; this is the safety net.
    refetchInterval: 60_000,
    retry: false,
  });
}

function useInvalidateSafety() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: safetyKeys.all });
}

export function useStartTimer() {
  const invalidate = useInvalidateSafety();
  return useMutation({
    mutationFn: async (body: {
      fileId: string;
      visitId?: string | null;
      expectedMinutes: number;
    }) => {
      const fix = await currentFix();
      return (await api.post<SafetyTimerView>('/safety/timers', { ...body, ...fix })).data;
    },
    onSuccess: invalidate,
  });
}

export function useCheckIn() {
  const invalidate = useInvalidateSafety();
  return useMutation({
    mutationFn: async ({ timerId, ...body }: { timerId: string } & SafetyCheckInDto) => {
      const fix = await currentFix();
      return (
        await api.post<SafetyTimerView>(`/safety/timers/${timerId}/check-in`, { ...body, ...fix })
      ).data;
    },
    onSuccess: invalidate,
  });
}

export function useEndTimer() {
  const invalidate = useInvalidateSafety();
  return useMutation({
    mutationFn: async (timerId: string) =>
      (await api.post<SafetyTimerView>(`/safety/timers/${timerId}/end`, {})).data,
    onSuccess: invalidate,
  });
}

/**
 * Raise an SOS. The caller passes the `clientId` it generated when the officer
 * confirmed; three quick retries reuse it, so a flaky connection can never
 * raise a second alarm, only make sure the first one lands.
 */
export async function sendSos(body: {
  clientId: string;
  fileId?: string | null;
  note?: string | null;
}): Promise<SafetyEventView[]> {
  const fix = await currentFix(3_000);
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return (await api.post<SafetyEventView[]>('/safety/sos', { ...body, ...fix })).data;
    } catch (err) {
      lastError = err;
      const status = (err as { response?: { status?: number } })?.response?.status;
      // A real answer (refused, invalid) will not change on retry.
      if (status && status < 500) throw err;
      await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
    }
  }
  throw lastError;
}

export function useRaiseSos() {
  const invalidate = useInvalidateSafety();
  return useMutation({ mutationFn: sendSos, onSuccess: invalidate });
}

export function useSafetyBoard(localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: safetyKeys.board(localGovernmentId ?? ''),
    queryFn: async () =>
      (await api.get<SafetyBoard>(`/safety/local-governments/${localGovernmentId}/board`)).data,
    enabled: !!localGovernmentId,
    refetchInterval: 30_000,
  });
}

export function useAcknowledge() {
  const invalidate = useInvalidateSafety();
  return useMutation({
    mutationFn: async (eventId: string) =>
      (await api.post<SafetyEventView>(`/safety/events/${eventId}/acknowledge`, {})).data,
    onSuccess: invalidate,
  });
}

export function useResolve() {
  const invalidate = useInvalidateSafety();
  return useMutation({
    mutationFn: async ({ eventId, note }: { eventId: string; note: string }) =>
      (await api.post<SafetyEventView>(`/safety/events/${eventId}/resolve`, { note })).data,
    onSuccess: invalidate,
  });
}

// ── Hazards ─────────────────────────────────────────────────────────────────

export function useHazards(fileId: string | undefined) {
  return useQuery({
    queryKey: safetyKeys.hazards(fileId ?? ''),
    queryFn: async () => (await api.get<FileHazards>(`/files/${fileId}/hazards`)).data,
    enabled: !!fileId,
  });
}

export function useHazardMap(enabled = true) {
  return useQuery({
    queryKey: safetyKeys.hazardMap,
    queryFn: async () => (await api.get<HazardMapEntry[]>('/safety/hazards/map')).data,
    enabled,
  });
}

function useHazardWrite<T>(fileId: string, fn: (body: T) => Promise<FileHazards>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.setQueryData(safetyKeys.hazards(fileId), data);
      void qc.invalidateQueries({ queryKey: safetyKeys.hazardMap });
    },
  });
}

export function useCreateHazard(fileId: string) {
  return useHazardWrite<CreateHazardDto>(
    fileId,
    async (body) => (await api.post<FileHazards>(`/files/${fileId}/hazards`, body)).data,
  );
}

export function useUpdateHazard(fileId: string) {
  return useHazardWrite<{ hazardId: string } & UpdateHazardDto>(
    fileId,
    async ({ hazardId, ...body }) =>
      (await api.patch<FileHazards>(`/files/${fileId}/hazards/${hazardId}`, body)).data,
  );
}

export function useClearHazard(fileId: string) {
  return useHazardWrite<{ hazardId: string } & ClearHazardDto>(
    fileId,
    async ({ hazardId, ...body }) =>
      (await api.post<FileHazards>(`/files/${fileId}/hazards/${hazardId}/clear`, body)).data,
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

/** Fold one pushed event into the session's list of pushed alerts. */
export function mergeEvent(
  list: SafetyEventView[] | undefined,
  event: SafetyEventView,
): SafetyEventView[] {
  return [event, ...(list ?? []).filter((e) => e.id !== event.id)];
}

/**
 * The alerts the shell banner shows: still open (nobody has acknowledged),
 * not the viewer's own alarm, newest first, one per event.
 */
export function bannerAlerts(
  sources: Array<SafetyEventView[] | undefined>,
  viewerId: string | undefined,
): SafetyEventView[] {
  const byId = new Map<string, SafetyEventView>();
  for (const list of sources) {
    for (const e of list ?? []) {
      const seen = byId.get(e.id);
      // A later status wins: an acknowledgement pushed after the board loaded.
      if (!seen || rank(e) > rank(seen)) byId.set(e.id, e);
    }
  }
  return [...byId.values()]
    .filter((e) => e.status === 'open' && e.officerId !== viewerId)
    .sort((a, b) => b.raisedAt.localeCompare(a.raisedAt));
}

function rank(e: SafetyEventView): number {
  return e.status === 'resolved' ? 2 : e.status === 'acknowledged' ? 1 : 0;
}

/** "12:05" / "1:02:05" for a span in milliseconds, never negative. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.round(Math.abs(ms) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** A Google Maps link to a coordinate, for a supervisor heading out. */
export function mapsLink(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/** Short time in the viewer's language. */
export function shortTime(iso: string, language: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso)),
  );
}
